import type { ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import styles from './WidgetCollapse.module.css';

/**
 * THE expand/collapse animation for every collapsible surface in chat — lifted verbatim from the
 * Linear widget, which is the reference. Anything in the thread that opens or closes goes through
 * here so the motion is identical everywhere.
 *
 * The spring opens with a subtle settle; the close uses a plain tween so height lands flat on 0
 * instead of the underdamped spring undershooting past it and bouncing back a frame — that bounce is
 * the end-of-collapse jitter. `initial={false}` keeps an already-open body from animating itself in
 * on mount, so a widget that arrives expanded doesn't unfold in front of you.
 *
 * Two rules for callers, which were the other half of the jumping:
 *  1. The collapsing element must CLIP — `.collapse` does that here so it can't be forgotten.
 *  2. Header→body spacing goes on the body's FIRST CHILD, never as a margin or border under the
 *     header: the body unmounts when collapsed, so header-side spacing stays behind as dead space
 *     and pops in/out instantly while the height is still animating.
 */
export const COLLAPSE_SPRING = { type: 'spring', stiffness: 500, damping: 30 } as const;
export const COLLAPSE_EXIT_TWEEN = { duration: 0.2, ease: 'easeInOut' } as const;

export interface WidgetCollapseProps {
  open: boolean;
  /** The caller's own layout class for the collapsing container (clipping is added here). */
  className?: string;
  children: ReactNode;
}

export const WidgetCollapse = ({ open, className, children }: WidgetCollapseProps) => {
  const reduceMotion = useReducedMotion();
  const openTransition = reduceMotion ? { duration: 0 } : COLLAPSE_SPRING;
  const exitTransition = reduceMotion ? { duration: 0 } : COLLAPSE_EXIT_TWEEN;

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          className={className ? `${styles.collapse} ${className}` : styles.collapse}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0, transition: exitTransition }}
          transition={openTransition}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
